/**
 * PriceModifiersSection
 *
 * SCRUM-1283: Guide Dashboard — Price Modifiers UI
 * Modifier types grouped by class. Enable/disable toggle, value entry
 * with min/max validation, human-readable effect display.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Sliders, Loader2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { getModifiers, addModifier, updateModifier, toggleModifier, deleteModifier } from '../../../services/guidePricingApi';
import { formatCentsToDollars } from '../../../utils/formHelpers';

function formatModifierEffect(valueUnit, value) {
  if (valueUnit === 'cents') {
    return formatCentsToDollars(value);
  }
  if (valueUnit === 'multiplier') {
    const pct = Math.round((value - 1) * 100);
    return pct >= 0 ? `+${pct}%` : `${pct}%`;
  }
  if (valueUnit === 'percentage') {
    return `${value}%`;
  }
  return String(value);
}

export default function PriceModifiersSection({ experienceId }) {
  const [modifierClasses, setModifierClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedClasses, setExpandedClasses] = useState(new Set());
  const [editingConfig, setEditingConfig] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const fetchModifiers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getModifiers(experienceId);
      setModifierClasses(res.data || []);
      // Auto-expand classes that have configs
      const expanded = new Set();
      for (const cls of (res.data || [])) {
        if (cls.configured) {
          expanded.add(cls.id);
        }
      }
      setExpandedClasses(expanded);
    } catch (err) {
      setError(err.message || 'Failed to load modifiers');
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    fetchModifiers();
  }, [fetchModifiers]);

  const toggleClassExpanded = (classId) => {
    setExpandedClasses(prev => {
      const next = new Set(prev);
      if (next.has(classId)) {
        next.delete(classId);
      } else {
        next.add(classId);
      }
      return next;
    });
  };

  const handleToggle = async (config, newEnabled) => {
    setTogglingId(config.id);
    try {
      if (newEnabled && !config.id) {
        // Enabling a type that doesn't have a config yet — need to create one
        // This shouldn't happen as the toggle is only shown for configured modifiers
        return;
      }
      await toggleModifier(experienceId, config.id, newEnabled);
      await fetchModifiers();
    } catch (err) {
      setError(err.message || 'Failed to toggle modifier');
    } finally {
      setTogglingId(null);
    }
  };

  const handleEnableType = async (modifierClass, type) => {
    setSaving(true);
    setEditError(null);
    try {
      await addModifier(experienceId, {
        pricingConfigId: null, // Will be resolved by the backend from active config
        modifierClassCode: modifierClass.code,
        modifierTypeId: type.id,
        adjustmentValue: parseFloat(type.default_value),
        stackOrder: type.display_order || 0
      });
      await fetchModifiers();
    } catch (err) {
      const msg = err.error?.message || err.message || 'Failed to enable modifier';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (config) => {
    setEditingConfig(config.id);
    setEditValue(String(config.value));
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingConfig(null);
    setEditValue('');
    setEditError(null);
  };

  const validateValue = (value, type) => {
    const num = parseFloat(value);
    if (isNaN(num)) { return 'Value must be a number'; }
    if (type.min_value !== null && type.min_value !== undefined && num < parseFloat(type.min_value)) {
      return `Value must be at least ${type.min_value}`;
    }
    if (type.max_value !== null && type.max_value !== undefined && num > parseFloat(type.max_value)) {
      return `Value must not exceed ${type.max_value}`;
    }
    return null;
  };

  const handleSaveValue = async (config, type) => {
    const validationError = validateValue(editValue, type);
    if (validationError) {
      setEditError(validationError);
      return;
    }

    setSaving(true);
    setEditError(null);
    try {
      await updateModifier(experienceId, config.id, {
        value: parseFloat(editValue)
      });
      setEditingConfig(null);
      await fetchModifiers();
    } catch (err) {
      const msg = err.error?.message || err.message || 'Failed to update modifier';
      setEditError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (configId) => {
    setSaving(true);
    try {
      await deleteModifier(experienceId, configId);
      await fetchModifiers();
    } catch (err) {
      setError(err.message || 'Failed to disable modifier');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sliders className="h-5 w-5 text-emerald-600" />
          <h3 className="text-lg font-semibold">Price Modifiers</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="price-modifiers-section">
      <div className="flex items-center gap-2 mb-4">
        <Sliders className="h-5 w-5 text-emerald-600" />
        <h3 className="text-lg font-semibold">Price Modifiers</h3>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-center gap-2 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600 text-xs">dismiss</button>
        </div>
      )}

      {modifierClasses.length === 0 ? (
        <div className="text-center py-8 text-gray-500" data-testid="modifiers-empty">
          <Sliders className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No modifier types available</p>
          <p className="text-xs text-gray-400 mt-1">Modifier types will appear once the platform seeds them</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="modifier-classes-list">
          {modifierClasses.map(cls => {
            const isExpanded = expandedClasses.has(cls.id);
            const configuredCount = (cls.configs || []).length;

            return (
              <div key={cls.id} className="border rounded-lg" data-testid="modifier-class-group">
                <button
                  onClick={() => toggleClassExpanded(cls.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
                  data-testid="modifier-class-header"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-gray-400" />
                      : <ChevronRight className="h-4 w-4 text-gray-400" />
                    }
                    <span className="font-medium text-sm">{cls.name}</span>
                    {configuredCount > 0 && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                        {configuredCount} active
                      </span>
                    )}
                  </div>
                  {cls.description && (
                    <span className="text-xs text-gray-400 hidden md:inline">{cls.description}</span>
                  )}
                </button>

                {isExpanded && (
                  <div className="border-t px-4 py-2 space-y-2" data-testid="modifier-types-list">
                    {(cls.types || []).map(type => {
                      const config = (cls.configs || []).find(c => c.modifier_type_id === type.id);
                      const isConfigured = !!config;
                      const isEditing = editingConfig === config?.id;
                      const isToggling = togglingId === config?.id;

                      return (
                        <div
                          key={type.id}
                          className="flex items-center justify-between py-2 px-2 rounded hover:bg-gray-50"
                          data-testid="modifier-type-row"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">{type.name}</span>
                              {isConfigured && config.is_enabled && (
                                <span className="text-xs text-emerald-600 font-medium" data-testid="modifier-effect">
                                  {formatModifierEffect(type.value_unit, parseFloat(config.value))}
                                </span>
                              )}
                              {isConfigured && !config.is_enabled && (
                                <span className="text-xs text-gray-400">(disabled)</span>
                              )}
                            </div>
                            {type.description && (
                              <p className="text-xs text-gray-400 truncate">{type.description}</p>
                            )}
                            {isConfigured && (
                              <p className="text-xs text-gray-400 mt-0.5" data-testid="modifier-stack-order">
                                Stack order: {config.stack_order}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            {isConfigured && isEditing ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  step="any"
                                  value={editValue}
                                  onChange={(e) => { setEditValue(e.target.value); setEditError(null); }}
                                  className={`w-24 border rounded px-2 py-1 text-sm ${editError ? 'border-red-500' : ''}`}
                                  data-testid="modifier-value-input"
                                />
                                <button
                                  onClick={() => handleSaveValue(config, type)}
                                  disabled={saving}
                                  className="text-xs text-emerald-600 hover:text-emerald-700 font-medium px-2 py-1"
                                  data-testid="modifier-save-btn"
                                >
                                  {saving ? '...' : 'Save'}
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="text-xs text-gray-400 hover:text-gray-600 px-1 py-1"
                                >
                                  Cancel
                                </button>
                                {editError && (
                                  <span className="text-xs text-red-600 ml-1" data-testid="modifier-value-error">{editError}</span>
                                )}
                              </div>
                            ) : isConfigured ? (
                              <>
                                <button
                                  onClick={() => startEdit(config)}
                                  className="text-xs text-gray-500 hover:text-emerald-600 px-2 py-1"
                                  data-testid="modifier-edit-btn"
                                >
                                  Edit
                                </button>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={config.is_enabled}
                                    onChange={(e) => handleToggle(config, e.target.checked)}
                                    disabled={isToggling}
                                    className="sr-only peer"
                                    data-testid="modifier-toggle"
                                  />
                                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
                                </label>
                                <button
                                  onClick={() => handleDisable(config.id)}
                                  className="text-xs text-red-400 hover:text-red-600 px-1 py-1"
                                  title="Remove"
                                  data-testid="modifier-remove-btn"
                                >
                                  Remove
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleEnableType(cls, type)}
                                disabled={saving}
                                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium px-2 py-1 border border-emerald-200 rounded"
                                data-testid="modifier-enable-btn"
                              >
                                Enable
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {(!cls.types || cls.types.length === 0) && (
                      <p className="text-sm text-gray-400 py-2">No modifier types in this class</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
